import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import axios from 'axios';
import { sanitizeInput, isValidEmail } from '../utils/sanitization';
import { Eye, EyeOff } from 'lucide-react';
import { initializeImageProtection } from '../utils/imageProtection';
import { initializeTextProtection } from '../utils/textProtection';
import '../styles/imageProtection.css';
import '../styles/textProtection.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adminRecoveryAvailable, setAdminRecoveryAvailable] = useState(false);
  const [branding, setBranding] = useState({
    loginTitle: 'Dog Grooming',
    loginSubtitle: 'Sign in to your account',
    loginTextColor: '#1f2937',
    loginFontFamily: 'system-ui',
    loginLogoLeft: {
      enabled: false,
      type: 'emoji',
      icon: '🐕',
      image: null,
      backgroundColor: '#2563eb',
      size: 48,
    },
    loginLogoAbove: {
      enabled: true,
      type: 'emoji',
      icon: '🐕',
      image: null,
      backgroundColor: '#2563eb',
      size: 64,
    },
    loginLogoRight: {
      enabled: false,
      type: 'emoji',
      icon: '🐕',
      image: null,
      backgroundColor: '#2563eb',
      size: 48,
    },
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadBranding();
    checkAdminRecoveryAvailable();
    
    // Initialize image protection
    const cleanupImages = initializeImageProtection();
    
    // Initialize text protection
    const cleanupText = initializeTextProtection();
    
    // Listen for branding update events
    const handleBrandingUpdate = () => {
      console.log('Branding update event received on Login page, reloading...');
      loadBranding();
    };
    
    window.addEventListener('brandingUpdated', handleBrandingUpdate);
    
    return () => {
      cleanupImages();
      cleanupText();
      window.removeEventListener('brandingUpdated', handleBrandingUpdate);
    };
  }, []);

  const checkAdminRecoveryAvailable = async () => {
    try {
      // Check if any admin has recovery configured
      const response = await axios.get(`${API_URL}/admin-security/check-availability`);
      setAdminRecoveryAvailable(response.data.available);
    } catch (error) {
      // If endpoint fails or doesn't exist, hide the button
      setAdminRecoveryAvailable(false);
    }
  };

  const loadBranding = async () => {
    try {
      const response = await axios.get(`${API_URL}/business-info/site_branding`);
      if (response.data.info_value) {
        const data = JSON.parse(response.data.info_value);
        setBranding({
          loginTitle: data.loginTitle || data.siteName || 'MorphBook',
          loginSubtitle: data.loginSubtitle || 'Sign in to your account',
          loginTextColor: data.loginTextColor || '#1f2937',
          loginFontFamily: data.loginFontFamily || 'system-ui',
          loginLogoLeft: {
            enabled: data.loginLogoLeft?.enabled || false,
            type: data.loginLogoLeft?.type || 'emoji',
            icon: data.loginLogoLeft?.icon || data.siteIcon || '🐕',
            image: data.loginLogoLeft?.image || null,
            backgroundColor: data.loginLogoLeft?.backgroundColor || data.logoBackgroundColor || '#2563eb',
            size: data.loginLogoLeft?.size || 48,
          },
          loginLogoAbove: {
            enabled: data.loginLogoAbove?.enabled ?? true,
            type: data.loginLogoAbove?.type || 'emoji',
            icon: data.loginLogoAbove?.icon || data.siteIcon || '🐕',
            image: data.loginLogoAbove?.image || null,
            backgroundColor: data.loginLogoAbove?.backgroundColor || data.logoBackgroundColor || '#2563eb',
            size: data.loginLogoAbove?.size || 64,
          },
          loginLogoRight: {
            enabled: data.loginLogoRight?.enabled || false,
            type: data.loginLogoRight?.type || 'emoji',
            icon: data.loginLogoRight?.icon || data.siteIcon || '🐕',
            image: data.loginLogoRight?.image || null,
            backgroundColor: data.loginLogoRight?.backgroundColor || data.logoBackgroundColor || '#2563eb',
            size: data.loginLogoRight?.size || 48,
          },
        });
      }
    } catch (error) {
      // Keep defaults if not found
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Sanitize email input (DON'T convert to lowercase yet - let backend handle it)
    const sanitizedEmail = sanitizeInput(email, {
      allowEmojis: false,
      allowHtml: false,
      maxLength: 255,
      allowSpecialChars: true
    });

    // Validate email format
    if (!isValidEmail(sanitizedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Password is NOT sanitized (it's hashed on backend)
    // Only check for excessively long passwords (DoS prevention)
    // Don't enforce minimum on login to allow existing accounts
    if (password.length > 100) {
      toast.error('Password is too long');
      return;
    }

    setLoading(true);

    try {
      await login(sanitizedEmail, password);
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Status:', error.response?.status);
      
      const errorMessage = error.response?.data?.error || 'Login failed. Please check your credentials.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="mb-8">
          <div className="flex flex-col items-center space-y-4">
            {/* Logo Above */}
            {branding.loginLogoAbove.enabled && (
              <div 
                className="rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                style={{ 
                  backgroundColor: branding.loginLogoAbove.backgroundColor,
                  width: `${branding.loginLogoAbove.size || 64}px`,
                  height: `${branding.loginLogoAbove.size || 64}px`
                }}
              >
                {branding.loginLogoAbove.type === 'image' && branding.loginLogoAbove.image ? (
                  <div className="protected-image-container w-full h-full">
                    <img src={branding.loginLogoAbove.image} alt="Logo Above" className="w-full h-full object-cover protected" draggable="false" onContextMenu={(e) => e.preventDefault()} />
                  </div>
                ) : (
                  <span 
                    className="font-bold"
                    style={{ 
                      color: '#ffffff',
                      fontSize: `${Math.max(16, (branding.loginLogoAbove.size || 64) * 0.5)}px`
                    }}
                  >
                    {branding.loginLogoAbove.icon}
                  </span>
                )}
              </div>
            )}
            
            {/* Title with Left and Right Logos */}
            <div className="flex items-center space-x-4">
              {/* Logo Left */}
              {branding.loginLogoLeft.enabled && (
                <div 
                  className="rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                  style={{ 
                    backgroundColor: branding.loginLogoLeft.backgroundColor,
                    width: `${branding.loginLogoLeft.size || 48}px`,
                    height: `${branding.loginLogoLeft.size || 48}px`
                  }}
                >
                  {branding.loginLogoLeft.type === 'image' && branding.loginLogoLeft.image ? (
                    <div className="protected-image-container w-full h-full">
                      <img src={branding.loginLogoLeft.image} alt="Logo Left" className="w-full h-full object-cover protected" draggable="false" onContextMenu={(e) => e.preventDefault()} />
                    </div>
                  ) : (
                    <span 
                      className="font-bold"
                      style={{ 
                        color: '#ffffff',
                        fontSize: `${Math.max(14, (branding.loginLogoLeft.size || 48) * 0.5)}px`
                      }}
                    >
                      {branding.loginLogoLeft.icon}
                    </span>
                  )}
                </div>
              )}
              
              {/* Title */}
              <h2 
                className="text-4xl font-bold text-center protected-text login-text-protected"
                style={{ 
                  color: branding.loginTextColor,
                  fontFamily: branding.loginFontFamily 
                }}
              >
                {branding.loginTitle}
              </h2>
              
              {/* Logo Right */}
              {branding.loginLogoRight.enabled && (
                <div 
                  className="rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                  style={{ 
                    backgroundColor: branding.loginLogoRight.backgroundColor,
                    width: `${branding.loginLogoRight.size || 48}px`,
                    height: `${branding.loginLogoRight.size || 48}px`
                  }}
                >
                  {branding.loginLogoRight.type === 'image' && branding.loginLogoRight.image ? (
                    <div className="protected-image-container w-full h-full">
                      <img src={branding.loginLogoRight.image} alt="Logo Right" className="w-full h-full object-cover protected" draggable="false" onContextMenu={(e) => e.preventDefault()} />
                    </div>
                  ) : (
                    <span 
                      className="font-bold"
                      style={{ 
                        color: '#ffffff',
                        fontSize: `${Math.max(14, (branding.loginLogoRight.size || 48) * 0.5)}px`
                      }}
                    >
                      {branding.loginLogoRight.icon}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <p className="mt-4 text-center text-gray-600 protected-text login-text-protected">{branding.loginSubtitle}</p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="label">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                required
                className="input"
                value={email}
                onChange={(e) => {
                  // Sanitize email input in real-time
                  const sanitized = sanitizeInput(e.target.value, {
                    allowEmojis: false,
                    allowHtml: false,
                    maxLength: 255,
                    allowSpecialChars: true
                  });
                  setEmail(sanitized);
                }}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="input pr-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onMouseDown={() => setShowPassword(true)}
                  onMouseUp={() => setShowPassword(false)}
                  onMouseLeave={() => setShowPassword(false)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 select-none"
                  tabIndex={-1}
                  title="Hold to reveal password"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn btn-primary"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-4 text-center space-y-2">
            <Link to="/forgot-password" className="text-sm text-primary-600 hover:text-primary-700 font-medium block">
              Forgot your password?
            </Link>
            {adminRecoveryAvailable && (
              <Link to="/admin-recovery" className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center justify-center space-x-1">
                <span>🛡️</span>
                <span>Admin Account Recovery</span>
              </Link>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
                Register here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
